/**
 * GovTaskItem — one checklist item as a rich task card.
 * Type-driven: each TaskType renders its own mini-form.
 * The checkbox is ALWAYS manually checked by the user (per Tyler's spec).
 */
import React, { useState, useEffect, useRef } from 'react';
import { TaskDef } from './govTasks';

interface Props {
  solicitationId: string;
  stage: string;
  task: TaskDef;
  checked: boolean;
  // The stored value for this task's artifact (may be object or scalar)
  artifact: any;
  // Solicitation fields that some tasks care about (POC, deadline, etc)
  solicitation: any;
  // Documents attached to this solicitation (for doc_attach tasks)
  documents: any[];
  users: Array<{ id: string; name?: string; email?: string }>;
  onToggle: (checked: boolean) => void;
  onArtifactChange: (artifact: any) => void;
  onSolicitationChange: (patch: Record<string, any>) => void;
  onAddEvent: (event: {
    kind: string; title: string; starts_at: string; ends_at?: string;
    description?: string; source_stage?: string; source_task_key?: string;
  }) => void;
  onOpenDocsTab: () => void;
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: '#1a2b3c', border: '1px solid #2d4259', borderRadius: 10,
    padding: 14, marginBottom: 12,
  },
  cardChecked: { borderColor: '#10b98155', background: '#0f3628' },
  head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
  checkbox: { width: 18, height: 18, cursor: 'pointer', accentColor: '#10b981', flexShrink: 0 },
  label: { fontSize: 14, fontWeight: 700, color: '#e5edf5', flex: 1 },
  ready: { fontSize: 11, background: '#10b98155', color: '#10b981', padding: '2px 8px', borderRadius: 10, fontWeight: 700 },
  hint: { fontSize: 12, color: '#8fa8c0', marginBottom: 8, fontStyle: 'italic' as const },
  fieldRow: { display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' as const },
  fieldFull: { flex: '1 1 100%' },
  input: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 6,
    padding: '8px 10px', color: '#e5edf5', fontSize: 13, width: '100%', boxSizing: 'border-box' as const,
  },
  labelSm: { fontSize: 11, color: '#8fa8c0', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4 },
  smallBtn: {
    background: '#4a7eb5', color: '#fff', border: 'none', borderRadius: 6,
    padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  ghostBtn: {
    background: 'transparent', color: '#8fa8c0', border: '1px solid #2d4259', borderRadius: 6,
    padding: '6px 12px', fontSize: 12, cursor: 'pointer',
  },
  docChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#243447', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: '#e5edf5',
    textDecoration: 'none', border: '1px solid #2d4259',
  },
  notesTa: {
    background: '#243447', border: '1px solid #2d4259', borderRadius: 6,
    padding: '8px 10px', color: '#e5edf5', fontSize: 12, width: '100%',
    minHeight: 40, resize: 'vertical' as const, boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  radio: { accentColor: '#4a7eb5' },
  savedNote: { fontSize: 10, color: '#10b981', marginLeft: 6 },
};

// Debounce hook for text fields
function useDebouncedSave<T>(value: T, onSave: (v: T) => void, delay = 700) {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const t = setTimeout(() => onSave(value), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
}

const GovTaskItem: React.FC<Props> = ({
  solicitationId, stage, task, checked, artifact, solicitation, documents, users,
  onToggle, onArtifactChange, onSolicitationChange, onAddEvent, onOpenDocsTab,
}) => {
  const [notes, setNotes] = useState<string>(artifact?.notes || '');
  useDebouncedSave(notes, (v) => onArtifactChange({ ...(artifact || {}), notes: v }));

  const attachedDocs = task.docCategory
    ? documents.filter((d) => d.category === task.docCategory)
    : [];

  const artifactReady = computeArtifactReady(task, artifact, solicitation, attachedDocs);

  return (
    <div style={{ ...S.card, ...(checked ? S.cardChecked : {}) }}>
      <div style={S.head}>
        <input type="checkbox" style={S.checkbox} checked={checked} onChange={(e) => onToggle(e.target.checked)} />
        <div style={S.label}>{task.label}</div>
        {!checked && artifactReady && <div style={S.ready}>Ready ✓</div>}
      </div>
      {task.hint && <div style={S.hint}>{task.hint}</div>}

      {/* Type-specific artifact form */}
      <TaskBody
        task={task}
        artifact={artifact}
        solicitation={solicitation}
        documents={documents}
        attachedDocs={attachedDocs}
        users={users}
        onArtifactChange={onArtifactChange}
        onSolicitationChange={onSolicitationChange}
        onAddEvent={(e) => onAddEvent({ ...e, source_stage: stage, source_task_key: task.key })}
        onOpenDocsTab={onOpenDocsTab}
      />

      {/* Universal notes */}
      <div style={{ marginTop: 8 }}>
        <div style={S.labelSm}>Notes</div>
        <textarea
          style={S.notesTa}
          placeholder="Optional notes about this task…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </div>
  );
};

// ─── Artifact-ready heuristic ────────────────────────────────────────────────
function computeArtifactReady(task: TaskDef, artifact: any, sol: any, attachedDocs: any[]): boolean {
  switch (task.type) {
    case 'notice_id':      return !!sol?.sam_notice_id;
    case 'docs_check':     return (attachedDocs.length || 0) > 0 || (Array.isArray(sol?.documents) && sol.documents.length > 0);
    case 'assignment':     return !!sol?.assigned_to;
    case 'bid_captain':    return !!sol?.bid_captain_id;
    case 'decision':       return !!sol?.go_no_go_decision;
    case 'naics_check':    return !!sol?.naics_code && !!artifact?.confirmed;
    case 'capability_check': return !!artifact?.fit_level;
    case 'poc_capture':    return !!sol?.primary_poc_name;
    case 'outreach':       return !!artifact?.method && !!artifact?.date;
    case 'teaming':        return artifact?.decision === 'no' || artifact?.decision === 'na' ||
                                  (artifact?.decision === 'yes' && !!artifact?.partners);
    case 'submission':     return !!sol?.bid_submitted_at || !!artifact?.submitted_at;
    case 'acknowledgment': return attachedDocs.length > 0 || !!artifact?.received_date;
    case 'follow_up_request': return !!artifact?.method && !!artifact?.date;
    case 'meeting':        return !!artifact?.event_id;
    case 'date_confirm':   return !!artifact?.confirmed_date;
    case 'notice_id_capture': return !!artifact?.actual_notice_id;
    case 'doc_attach':     return attachedDocs.length > 0;
    case 'field_text':     return task.field ? !!sol?.[task.field] : false;
    case 'field_money':    return task.field ? sol?.[task.field] != null : false;
    case 'field_date':     return task.field ? !!sol?.[task.field] : false;
    case 'wage_det':       return attachedDocs.length > 0 || !!artifact?.reference_number;
    case 'site_visit':     return !!artifact?.attended || artifact?.na === true;
    case 'cost_model':     return attachedDocs.length > 0 || artifact?.estimated_bid != null;
    default:               return false;
  }
}

// ─── Body renderers per task type ────────────────────────────────────────────
const TaskBody: React.FC<{
  task: TaskDef; artifact: any; solicitation: any; documents: any[]; attachedDocs: any[];
  users: Array<{ id: string; name?: string; email?: string }>;
  onArtifactChange: (a: any) => void;
  onSolicitationChange: (patch: Record<string, any>) => void;
  onAddEvent: (e: any) => void;
  onOpenDocsTab: () => void;
}> = ({ task, artifact, solicitation, attachedDocs, users, onArtifactChange, onSolicitationChange, onAddEvent, onOpenDocsTab }) => {
  const a = artifact || {};
  const sol = solicitation || {};

  switch (task.type) {
    case 'notice_id':
      return (
        <div style={S.fieldRow}>
          <div style={S.fieldFull}>
            <div style={S.labelSm}>SAM.gov Notice ID</div>
            <div style={{ color: '#e5edf5', fontSize: 13, fontFamily: 'monospace' }}>
              {sol.sam_notice_id || '(not set)'}
            </div>
          </div>
          {sol.sam_ui_link && (
            <a href={sol.sam_ui_link} target="_blank" rel="noreferrer" style={{ ...S.smallBtn, textDecoration: 'none' }}>
              Open on SAM.gov ↗
            </a>
          )}
        </div>
      );

    case 'docs_check': {
      const total = attachedDocs.length || 0;
      return (
        <div style={S.fieldRow}>
          <div style={{ ...S.fieldFull, color: '#e5edf5', fontSize: 13 }}>
            {total > 0 ? `${total} document${total === 1 ? '' : 's'} downloaded from SAM.gov.` : 'No documents attached yet.'}
          </div>
          <button style={S.ghostBtn} onClick={onOpenDocsTab}>Open Documents tab →</button>
        </div>
      );
    }

    case 'assignment':
    case 'bid_captain': {
      const field = task.type === 'assignment' ? 'assigned_to' : 'bid_captain_id';
      return (
        <div style={S.fieldRow}>
          <div style={S.fieldFull}>
            <div style={S.labelSm}>{task.type === 'assignment' ? 'Assigned Bidder' : 'Bid Captain'}</div>
            <select style={S.input} value={sol[field] || ''}
              onChange={(e) => onSolicitationChange({ [field]: e.target.value || null })}>
              <option value="">— Unassigned —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
        </div>
      );
    }

    case 'decision':
      return (
        <>
          <div style={S.fieldRow}>
            <div style={S.fieldFull}>
              <div style={S.labelSm}>Decision</div>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={{ color: '#e5edf5', fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name={`decision-${task.key}`} style={S.radio}
                    checked={sol.go_no_go_decision === 'go'}
                    onChange={() => onSolicitationChange({ go_no_go_decision: 'go' })} />
                  {' '}✅ Go
                </label>
                <label style={{ color: '#e5edf5', fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name={`decision-${task.key}`} style={S.radio}
                    checked={sol.go_no_go_decision === 'no_go'}
                    onChange={() => onSolicitationChange({ go_no_go_decision: 'no_go' })} />
                  {' '}❌ No-Go
                </label>
              </div>
            </div>
          </div>
          <div style={S.fieldRow}>
            <div style={S.fieldFull}>
              <div style={S.labelSm}>Reason</div>
              <textarea style={S.notesTa} placeholder="Why?"
                value={sol.go_no_go_reason || ''}
                onChange={(e) => onSolicitationChange({ go_no_go_reason: e.target.value })} />
            </div>
          </div>
        </>
      );

    case 'naics_check':
      return (
        <div style={S.fieldRow}>
          <div style={S.fieldFull}>
            <div style={S.labelSm}>NAICS on this solicitation</div>
            <div style={{ color: '#e5edf5', fontSize: 14 }}>
              <b>{sol.naics_code || '—'}</b>{sol.naics_description ? ` · ${sol.naics_description}` : ''}
            </div>
            <label style={{ color: '#e5edf5', fontSize: 13, cursor: 'pointer', marginTop: 8, display: 'inline-block' }}>
              <input type="checkbox" style={S.radio}
                checked={!!a.confirmed}
                onChange={(e) => onArtifactChange({ confirmed: e.target.checked })} />
              {' '}Confirmed match to our capabilities (238160, 238110, 237310, 236220, 238990)
            </label>
          </div>
        </div>
      );

    case 'capability_check':
      return (
        <div style={S.fieldRow}>
          <div style={S.fieldFull}>
            <div style={S.labelSm}>Fit Level</div>
            <div style={{ display: 'flex', gap: 12 }}>
              {[
                { v: 'strong', l: '💪 Strong' },
                { v: 'moderate', l: '👌 Moderate' },
                { v: 'weak', l: '⚠️ Weak' },
              ].map((opt) => (
                <label key={opt.v} style={{ color: '#e5edf5', fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name={`fit-${task.key}`} style={S.radio}
                    checked={a.fit_level === opt.v}
                    onChange={() => onArtifactChange({ fit_level: opt.v })} />
                  {' '}{opt.l}
                </label>
              ))}
            </div>
          </div>
        </div>
      );

    case 'poc_capture':
      return (
        <div style={S.fieldRow}>
          <div style={{ flex: '1 1 200px' }}>
            <div style={S.labelSm}>Name</div>
            <input style={S.input} value={sol.primary_poc_name || ''}
              onChange={(e) => onSolicitationChange({ primary_poc_name: e.target.value })} />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <div style={S.labelSm}>Email</div>
            <input style={S.input} type="email" value={sol.primary_poc_email || ''}
              onChange={(e) => onSolicitationChange({ primary_poc_email: e.target.value })} />
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <div style={S.labelSm}>Phone</div>
            <input style={S.input} value={sol.primary_poc_phone || ''}
              onChange={(e) => onSolicitationChange({ primary_poc_phone: e.target.value })} />
          </div>
        </div>
      );

    case 'outreach':
      return (
        <div style={S.fieldRow}>
          <div style={{ flex: '1 1 140px' }}>
            <div style={S.labelSm}>Method</div>
            <select style={S.input} value={a.method || ''}
              onChange={(e) => onArtifactChange({ method: e.target.value })}>
              <option value="">— select —</option>
              <option value="email">📧 Email</option>
              <option value="call">📞 Call</option>
              <option value="linkedin">💼 LinkedIn</option>
              <option value="in_person">🤝 In person</option>
            </select>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <div style={S.labelSm}>Date</div>
            <input style={S.input} type="date" value={a.date || ''}
              onChange={(e) => onArtifactChange({ date: e.target.value })} />
          </div>
        </div>
      );

    case 'teaming':
      return (
        <>
          <div style={S.fieldRow}>
            <div style={S.fieldFull}>
              <div style={S.labelSm}>Teaming Decision</div>
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { v: 'yes', l: '👥 Yes — team' }, { v: 'no', l: '🔒 No — solo' }, { v: 'na', l: '➖ N/A' },
                ].map((opt) => (
                  <label key={opt.v} style={{ color: '#e5edf5', fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name={`teaming-${task.key}`} style={S.radio}
                      checked={a.decision === opt.v}
                      onChange={() => onArtifactChange({ decision: opt.v })} />
                    {' '}{opt.l}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {a.decision === 'yes' && (
            <div style={S.fieldRow}>
              <div style={S.fieldFull}>
                <div style={S.labelSm}>Partners (name + POC)</div>
                <textarea style={S.notesTa} placeholder="Acme Roofing (john@acme.com, 555-...)…"
                  value={a.partners || ''}
                  onChange={(e) => onArtifactChange({ partners: e.target.value })} />
              </div>
            </div>
          )}
        </>
      );

    case 'submission':
      return (
        <div style={S.fieldRow}>
          <div style={{ flex: '1 1 140px' }}>
            <div style={S.labelSm}>Method</div>
            <select style={S.input} value={sol.bid_submission_method || ''}
              onChange={(e) => onSolicitationChange({ bid_submission_method: e.target.value })}>
              <option value="">— select —</option>
              <option value="sam_gov">SAM.gov portal</option>
              <option value="email">Email</option>
              <option value="mail">Mail / delivery</option>
              <option value="upload">Portal upload</option>
            </select>
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <div style={S.labelSm}>Submitted at</div>
            <input style={S.input} type="datetime-local"
              value={sol.bid_submitted_at ? sol.bid_submitted_at.slice(0, 16) : ''}
              onChange={(e) => onSolicitationChange({ bid_submitted_at: e.target.value })} />
          </div>
          <div style={{ ...S.fieldFull, marginTop: 4 }}>
            <div style={S.labelSm}>Confirmation #</div>
            <input style={S.input} value={sol.bid_submission_confirmation || ''}
              onChange={(e) => onSolicitationChange({ bid_submission_confirmation: e.target.value })} />
          </div>
        </div>
      );

    case 'acknowledgment':
      return (
        <>
          <div style={S.fieldRow}>
            <div style={{ flex: '1 1 140px' }}>
              <div style={S.labelSm}>Received on</div>
              <input style={S.input} type="date" value={a.received_date || ''}
                onChange={(e) => onArtifactChange({ received_date: e.target.value })} />
            </div>
          </div>
          <div style={{ color: '#8fa8c0', fontSize: 12 }}>
            {attachedDocs.length > 0
              ? `📎 ${attachedDocs.length} acknowledgment doc(s) attached`
              : 'Upload the acknowledgment email / receipt in the Documents tab.'}
          </div>
        </>
      );

    case 'follow_up_request':
      return (
        <div style={S.fieldRow}>
          <div style={{ flex: '1 1 140px' }}>
            <div style={S.labelSm}>Method</div>
            <select style={S.input} value={a.method || ''}
              onChange={(e) => onArtifactChange({ method: e.target.value })}>
              <option value="">—</option>
              <option value="email">📧 Email</option>
              <option value="call">📞 Call</option>
              <option value="meeting">🤝 Meeting</option>
            </select>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <div style={S.labelSm}>Requested date</div>
            <input style={S.input} type="date" value={a.date || ''}
              onChange={(e) => onArtifactChange({ date: e.target.value })} />
          </div>
        </div>
      );

    case 'meeting':
      return <MeetingScheduler artifact={a} onArtifactChange={onArtifactChange} onAddEvent={onAddEvent} />;

    case 'date_confirm':
      return (
        <div style={S.fieldRow}>
          <div style={{ flex: '1 1 200px' }}>
            <div style={S.labelSm}>Expected release date</div>
            <input style={S.input} type="date" value={a.confirmed_date || ''}
              onChange={(e) => onArtifactChange({ confirmed_date: e.target.value })} />
          </div>
        </div>
      );

    case 'notice_id_capture':
      return (
        <div style={S.fieldRow}>
          <div style={S.fieldFull}>
            <div style={S.labelSm}>Actual solicitation Notice ID / Sol #</div>
            <input style={S.input} placeholder="e.g. 36C24626Q0889"
              value={a.actual_notice_id || ''}
              onChange={(e) => onArtifactChange({ actual_notice_id: e.target.value })} />
            <div style={{ fontSize: 11, color: '#8fa8c0', marginTop: 4 }}>
              After capturing, use "Convert to Active" to spin up the active pipeline record.
            </div>
          </div>
        </div>
      );

    case 'doc_attach':
      return (
        <div>
          {attachedDocs.length > 0 ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {attachedDocs.map((d) => (
                <a key={d.id} style={S.docChip} href={d.storage_url} target="_blank" rel="noreferrer">
                  📎 {d.file_name.length > 30 ? d.file_name.slice(0, 30) + '…' : d.file_name}
                </a>
              ))}
            </div>
          ) : (
            <div style={{ color: '#8fa8c0', fontSize: 12 }}>
              No {task.docCategory} docs attached. <button style={{ ...S.ghostBtn, marginLeft: 6 }} onClick={onOpenDocsTab}>Upload in Documents →</button>
            </div>
          )}
        </div>
      );

    case 'field_text':
    case 'field_money':
    case 'field_date': {
      const field = task.field!;
      const type = task.type === 'field_money' ? 'number' : task.type === 'field_date' ? 'date' : 'text';
      const val = sol[field] ?? '';
      return (
        <div style={S.fieldRow}>
          <div style={S.fieldFull}>
            <input style={S.input} type={type}
              value={type === 'date' && val ? String(val).slice(0, 10) : val}
              onChange={(e) => onSolicitationChange({
                [field]: type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value,
              })} />
          </div>
        </div>
      );
    }

    case 'wage_det':
      return (
        <>
          <div style={S.fieldRow}>
            <div style={{ flex: '1 1 200px' }}>
              <div style={S.labelSm}>Reference #</div>
              <input style={S.input} placeholder="e.g. WD-2025-4218"
                value={a.reference_number || ''}
                onChange={(e) => onArtifactChange({ reference_number: e.target.value })} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <div style={S.labelSm}>Effective Date</div>
              <input style={S.input} type="date" value={a.effective_date || ''}
                onChange={(e) => onArtifactChange({ effective_date: e.target.value })} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#8fa8c0' }}>
            {attachedDocs.length > 0
              ? `📎 ${attachedDocs.length} wage determination doc(s) attached`
              : 'Upload the WD PDF from SAM.gov in the Documents tab.'}
          </div>
        </>
      );

    case 'site_visit':
      return (
        <>
          <div style={S.fieldRow}>
            <div style={S.fieldFull}>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ color: '#e5edf5', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" style={S.radio}
                    checked={!!a.na}
                    onChange={(e) => onArtifactChange({ na: e.target.checked, attended: false })} />
                  {' '}No site visit required (N/A)
                </label>
                <label style={{ color: '#e5edf5', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" style={S.radio}
                    checked={!!a.attended}
                    onChange={(e) => onArtifactChange({ attended: e.target.checked, na: false })} />
                  {' '}Attended
                </label>
              </div>
            </div>
          </div>
          {!a.na && (
            <div style={S.fieldRow}>
              <div style={{ flex: '1 1 140px' }}>
                <div style={S.labelSm}>Visit Date</div>
                <input style={S.input} type="date" value={a.date || ''}
                  onChange={(e) => onArtifactChange({ date: e.target.value })} />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <div style={S.labelSm}>Attendee(s)</div>
                <input style={S.input} placeholder="Names…"
                  value={a.attendees || ''}
                  onChange={(e) => onArtifactChange({ attendees: e.target.value })} />
              </div>
            </div>
          )}
        </>
      );

    case 'cost_model':
      return (
        <div style={S.fieldRow}>
          <div style={{ flex: '1 1 200px' }}>
            <div style={S.labelSm}>Estimated Bid $</div>
            <input style={S.input} type="number" placeholder="0"
              value={a.estimated_bid ?? ''}
              onChange={(e) => onArtifactChange({ estimated_bid: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
          <div style={{ flex: '1 1 120px' }}>
            <div style={S.labelSm}>Margin %</div>
            <input style={S.input} type="number" placeholder="0"
              value={a.margin_pct ?? ''}
              onChange={(e) => onArtifactChange({ margin_pct: e.target.value === '' ? null : Number(e.target.value) })} />
          </div>
          <div style={{ ...S.fieldFull, marginTop: 4, fontSize: 12, color: '#8fa8c0' }}>
            {attachedDocs.length > 0
              ? `📎 ${attachedDocs.length} cost model file(s) attached`
              : 'Upload your cost/pricing spreadsheet in the Documents tab (category: internal).'}
          </div>
        </div>
      );

    case 'simple':
    default:
      return null; // just the checkbox + notes
  }
};

// ─── Meeting scheduler subcomponent ──────────────────────────────────────────
const MeetingScheduler: React.FC<{
  artifact: any;
  onArtifactChange: (a: any) => void;
  onAddEvent: (e: any) => void;
}> = ({ artifact, onArtifactChange, onAddEvent }) => {
  const a = artifact || {};
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: a.title || '',
    starts_at: a.starts_at || '',
    location: a.location || '',
  });

  if (a.event_id) {
    return (
      <div style={{ color: '#e5edf5', fontSize: 13 }}>
        ✅ Meeting scheduled: <b>{a.title}</b>
        {a.starts_at && <span style={{ color: '#8fa8c0' }}> · {new Date(a.starts_at).toLocaleString()}</span>}
      </div>
    );
  }

  if (!creating) {
    return (
      <button style={S.smallBtn} onClick={() => setCreating(true)}>+ Schedule meeting</button>
    );
  }

  return (
    <div>
      <div style={S.fieldRow}>
        <div style={S.fieldFull}>
          <div style={S.labelSm}>Meeting Title</div>
          <input style={S.input} value={form.title} placeholder="Capability Brief w/ VA CO"
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
      </div>
      <div style={S.fieldRow}>
        <div style={{ flex: '1 1 200px' }}>
          <div style={S.labelSm}>Date & Time</div>
          <input style={S.input} type="datetime-local" value={form.starts_at}
            onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <div style={S.labelSm}>Location</div>
          <input style={S.input} placeholder="Zoom / office / TBD" value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={S.smallBtn}
          disabled={!form.title || !form.starts_at}
          onClick={async () => {
            onAddEvent({
              kind: 'team_meeting',
              title: form.title,
              starts_at: new Date(form.starts_at).toISOString(),
              description: '(scheduled from checklist task)',
            });
            onArtifactChange({ ...form, event_id: 'pending' });
            setCreating(false);
          }}>Save</button>
        <button style={S.ghostBtn} onClick={() => setCreating(false)}>Cancel</button>
      </div>
    </div>
  );
};

export default GovTaskItem;
